import React, { Fragment, useState, useEffect } from "react";
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { H3, Breadcrumbs, Btn } from "../../../AbstractElements";
import { getDataWithToken, postDataWithToken } from "../../../Utils/restUtils";
import {
  getCompanyModule,
  updateCompanyModule,
  getCompanyList,
  getIndustryData,
  getModuleList,
} from "../../../CommonURL";
import Typeahead from "../../../CommonElements/TypeAhead";
import Transparentspinner from "../../Common/loader/Transparentspinner";

const UserModuleEdit = () => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const companyModuleRecordId = searchParams.get("id") || searchParams.get("company_module_id");
  const selectedIndustry = watch("industry_type");

  useEffect(() => {
    initData();
  }, [companyModuleRecordId]);

  const fetchCompaniesByIndustry = async (industryId) => {
    if (!industryId) {
      setCompanyOptions([]);
      return [];
    }
    setLoadingCompanies(true);
    try {
      let res = await getDataWithToken(
        `${getCompanyList}?industry_type_id=${industryId}`
      );
      let list = res?.data?.data || res?.data || (Array.isArray(res) ? res : []);

      if (!list || list.length === 0) {
        const postRes = await postDataWithToken(getCompanyList, {
          industry_id: industryId,
          industry_type_id: industryId,
        });
        list = postRes?.data?.data || postRes?.data || (Array.isArray(postRes) ? postRes : []);
      }

      if (Array.isArray(list)) {
        const mapped = list.map((item) => ({
          value: item.id || item.company_id,
          label:
            item.address && item.address !== "null" && item.address !== null
              ? `${item.name || item.company_name} - ${item.address}`
              : item.name || item.company_name || `Company ${item.id}`,
          company_name: item.name || item.company_name || "",
        }));
        setCompanyOptions(mapped);
        return mapped;
      } else {
        setCompanyOptions([]);
        return [];
      }
    } catch (error) {
      console.error("Error fetching companies for industry:", error);
      setCompanyOptions([]);
      return [];
    } finally {
      setLoadingCompanies(false);
    }
  };

  const initData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Industries
      let indOptions = [];
      const industryRes = await getDataWithToken(getIndustryData);
      const industryList =
        industryRes?.data?.data ||
        industryRes?.data ||
        (Array.isArray(industryRes) ? industryRes : []);
      if (Array.isArray(industryList) && industryList.length > 0) {
        indOptions = industryList.map((ind) => ({
          value: ind.id,
          label: ind.name || `Industry ${ind.id}`,
        }));
        setIndustryOptions(indOptions);
      }

      // 2. Fetch Modules
      let mOptions = [];
      const moduleRes = await getDataWithToken(getModuleList);
      const modulesList =
        moduleRes?.data?.data ||
        moduleRes?.data ||
        (Array.isArray(moduleRes) ? moduleRes : []);
      if (Array.isArray(modulesList) && modulesList.length > 0) {
        mOptions = modulesList.map((m) => ({
          value: m.id,
          label: m.name || `Module ${m.id}`,
        }));
        setModuleOptions(mOptions);
      }

      // 3. Fetch Details by ID
      if (companyModuleRecordId) {
        const response = await getDataWithToken(
          `${getCompanyModule}?id=${companyModuleRecordId}&company_module_id=${companyModuleRecordId}`
        );
        const item =
          response?.data?.data?.[0] ||
          (Array.isArray(response?.data) ? response.data[0] : response?.data) ||
          (Array.isArray(response) ? response[0] : response);

        if (item) {
          let cOptions = [];
          if (item.industry_type_id || item.industryTypeId) {
            const indIdVal = item.industry_type_id || item.industryTypeId;
            const indMatch = indOptions.find((i) => i.value === indIdVal);
            setValue(
              "industry_type",
              indMatch || {
                value: indIdVal,
                label: item.industry_type_name || `Industry ${indIdVal}`,
              }
            );
            // Fetch companies for this industry
            cOptions = await fetchCompaniesByIndustry(indIdVal);
          }

          if (item.company_id || item.companyId || item.user_id || item.userId) {
            const companyIdVal =
              item.company_id || item.companyId || item.user_id || item.userId;
            const compName = item.company_name || item.user_name || item.name;
            const compMatch = cOptions.find((c) => c.value === companyIdVal);
            setValue(
              "company",
              compMatch || {
                value: companyIdVal,
                label: compName || `Company ${companyIdVal}`,
                company_name: compName,
              }
            );
          }

          if (item.module_id || item.moduleId) {
            const moduleIdVal = item.module_id || item.moduleId;
            const moduleMatch = mOptions.find((m) => m.value === moduleIdVal);
            setValue(
              "module",
              moduleMatch || {
                value: moduleIdVal,
                label: item.module_name || `Module ${moduleIdVal}`,
              }
            );
          }
        }
      }
    } catch (error) {
      console.error("Error initializing company module edit data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIndustryChange = (selectedOption) => {
    setValue("industry_type", selectedOption, { shouldValidate: true });
    setValue("company", null);
    setCompanyOptions([]);

    if (selectedOption?.value) {
      fetchCompaniesByIndustry(selectedOption.value);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const companyName =
      data.company?.company_name ||
      (data.company?.label?.includes(" - ")
        ? data.company.label.split(" - ")[0]
        : data.company?.label);

    const postRequest = {
      company_module_id: companyModuleRecordId,
      company_id: data.company?.value,
      company_name: companyName,
      module_id: data.module?.value,
      industry_type_id: data.industry_type?.value,
    };

    try {
      const response = await postDataWithToken(
        `${updateCompanyModule}?company_module_id=${companyModuleRecordId}`,
        postRequest
      );
      if (
        response?.data?.status === true ||
        response?.status === 200 ||
        response?.data?.message === "success"
      ) {
        toast.success(
          response?.data?.message || "Company Module updated successfully"
        );
        navigate(`${process.env.PUBLIC_URL}/configuration/company-modules`);
      } else {
        toast.error(response?.data?.message || "Failed to update company module");
      }
    } catch (error) {
      console.error("Error updating company module:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`${process.env.PUBLIC_URL}/configuration/company-modules`);
  };

  return (
    <Fragment>
      {(loading || loadingCompanies) && <Transparentspinner />}

      <Breadcrumbs
        mainTitle="Edit Company Module"
        parent="Configuration"
        title="Edit Company Module"
      />

      <Card>
        <CardHeader className="pb-0">
          <H3>Edit Company Module</H3>
        </CardHeader>
        <CardBody>
          <Form
            className="needs-validation"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Row className="g-3">
              {/* Industry Type Dropdown */}
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="industry_type">
                    Industry Type <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="industry_type"
                    control={control}
                    rules={{ required: "Please select an industry type" }}
                    render={({ field }) => (
                      <Typeahead
                        options={industryOptions}
                        value={field.value}
                        onChange={(selected) => handleIndustryChange(selected)}
                        placeholder="Select Industry Type"
                      />
                    )}
                  />
                  {errors.industry_type && (
                    <span className="text-danger small">
                      {errors.industry_type.message}
                    </span>
                  )}
                </FormGroup>
              </Col>

              {/* Company Dropdown (dependent on selected Industry) */}
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="company">
                    Company <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="company"
                    control={control}
                    rules={{ required: "Please select a company" }}
                    render={({ field }) => (
                      <Typeahead
                        options={companyOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={
                          selectedIndustry?.value
                            ? loadingCompanies
                              ? "Loading companies..."
                              : companyOptions.length > 0
                              ? "Select Company"
                              : "No companies found for this industry"
                            : "Select Industry first"
                        }
                        disabled={!selectedIndustry?.value || loadingCompanies}
                      />
                    )}
                  />
                  {errors.company && (
                    <span className="text-danger small">
                      {errors.company.message}
                    </span>
                  )}
                </FormGroup>
              </Col>

              {/* Module Dropdown */}
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="module">
                    Module <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="module"
                    control={control}
                    rules={{ required: "Please select a module" }}
                    render={({ field }) => (
                      <Typeahead
                        options={moduleOptions}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select Module"
                      />
                    )}
                  />
                  {errors.module && (
                    <span className="text-danger small">
                      {errors.module.message}
                    </span>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-4">
              <Btn
                attrBtn={{
                  color: "primary",
                  type: "submit",
                  disabled: loading || loadingCompanies,
                }}
              >
                Update Company Module
              </Btn>
              <Btn
                attrBtn={{
                  color: "secondary",
                  type: "button",
                  disabled: loading,
                  onClick: handleCancel,
                }}
              >
                Cancel
              </Btn>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default UserModuleEdit;
